import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Grid,
    CircularProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Card,
    IconButton
} from '@mui/material';
import { Download, FilterList, Assessment } from '@mui/icons-material';
import api from '../services/api';

const ReportExport = () => {
    const [dates, setDates] = useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [filters, setFilters] = useState({
        departmentId: '',
        programId: '',
        subjectId: ''
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [departments, setDepartments] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [subjects, setSubjects] = useState([]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    // Reactive fetching: Auto show data when dates or filters change
    useEffect(() => {
        fetchAttendance();
    }, [dates, filters]);

    const fetchInitialData = async () => {
        try {
            const [deptRes, progRes, subRes] = await Promise.all([
                api.get('/admin/departments'),
                api.get('/admin/programs'),
                api.get('/admin/subjects')
            ]);
            setDepartments(deptRes.data || []);
            setPrograms(progRes.data || []);
            setSubjects(subRes.data || []);
        } catch (error) {
            console.error('Failed to fetch filter data:', error);
        }
    };

    const fetchAttendance = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/reports/attendance', {
                params: { ...dates, ...filters }
            });
            setData(response.data || []);
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const response = await api.get('/admin/reports/export-csv', {
                params: { ...dates, ...filters },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `attendance_${dates.startDate}_to_${dates.endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            alert('Export failed.');
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Box p={4}>
            {/* Custom Header From Image Inspiration */}
            <Card sx={{ p: 2, mb: 4, display: 'flex', alignItems: 'center', borderRadius: 2 }}>
                <Box sx={{ 
                    backgroundColor: '#A855F7', 
                    p: 1.5, 
                    borderRadius: 2, 
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Assessment sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" fontWeight="bold">Attendance Reports</Typography>
                    <Typography variant="body1" color="text.secondary">View and export attendance reports</Typography>
                </Box>
            </Card>

            <Paper sx={{ p: 4, mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" mb={3} display="flex" alignItems="center">
                    <FilterList sx={{ mr: 1, fontSize: 28 }} /> Filters
                </Typography>
                <Grid container spacing={3} alignItems="flex-end">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Start Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true, sx: { fontSize: '1.2rem' } }}
                            value={dates.startDate}
                            onChange={(e) => setDates({ ...dates, startDate: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="End Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true, sx: { fontSize: '1.2rem' } }}
                            value={dates.endDate}
                            onChange={(e) => setDates({ ...dates, endDate: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            label="Faculty"
                            fullWidth
                            InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                            value={filters.departmentId}
                            onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })}
                        >
                            <MenuItem value="" sx={{ fontSize: '1rem' }}>All Faculties</MenuItem>
                            {departments.map(dept => (
                                <MenuItem key={dept.id} value={dept.id} sx={{ fontSize: '1rem' }}>{dept.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            label="Program"
                            fullWidth
                            InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                            value={filters.programId}
                            onChange={(e) => setFilters({ ...filters, programId: e.target.value })}
                        >
                            <MenuItem value="" sx={{ fontSize: '1rem' }}>All Programs</MenuItem>
                            {programs
                                .filter(p => !filters.departmentId || String(p.department_id) === String(filters.departmentId))
                                .map(prog => (
                                    <MenuItem key={prog.id} value={prog.id} sx={{ fontSize: '1rem' }}>{prog.name}</MenuItem>
                                ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            label="Subject"
                            fullWidth
                            InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                            value={filters.subjectId}
                            onChange={(e) => setFilters({ ...filters, subjectId: e.target.value })}
                        >
                            <MenuItem value="" sx={{ fontSize: '1rem' }}>All Subjects</MenuItem>
                            {subjects
                                .filter(s => !filters.programId || String(s.program_id) === String(filters.programId))
                                .map(sub => (
                                    <MenuItem key={sub.id} value={sub.id} sx={{ fontSize: '1rem' }}>{sub.name}</MenuItem>
                                ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button 
                            variant="contained" 
                            size="large"
                            onClick={fetchAttendance} 
                            disabled={loading}
                            sx={{ mr: 2, px: 4, py: 1.2, fontWeight: 'bold' }}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={exportLoading ? <CircularProgress size={20} /> : <Download />}
                            onClick={handleExport}
                            disabled={exportLoading}
                            sx={{ px: 4, py: 1.2, fontWeight: 'bold' }}
                        >
                            {exportLoading ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                {loading ? (
                    <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>
                ) : (
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Student Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Roll Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Subject</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Faculty/Program</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((record) => (
                                    <TableRow key={record.id} hover>
                                        <TableCell sx={{ fontSize: '1rem' }}>{new Date(record.date).toLocaleDateString()}</TableCell>
                                        <TableCell sx={{ fontSize: '1rem' }}>{record.time_in}</TableCell>
                                        <TableCell sx={{ fontSize: '1rem', fontWeight: 500 }}>{record.student_name}</TableCell>
                                        <TableCell sx={{ fontSize: '1rem' }}>{record.roll_number}</TableCell>
                                        <TableCell sx={{ fontSize: '1rem' }}>{record.subject_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                                {record.faculty_name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {record.program_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                color={record.status.toUpperCase() === 'PRESENT' ? 'success.main' : 'error.main'}
                                                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                                            >
                                                {record.status}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3, fontSize: '1.2rem' }}>No records found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Box>
    );
};

export default ReportExport;